import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useHistory } from 'react-router-dom';
import { AxiosResponse } from 'axios';
import EInkModel from '../../models/EInk';

import ApiResponse from '../../services/ApiResponse';
import EInkService from '../../services/use_case/eink/EInkService';

import {
    DashboardTitle,
    DashboardWrapper,
    DetailBreadcrumb,
} from '../../components';

interface EInkDetailProps {};

interface RotationOption {
    label: string;
    value: string;
    degrees: number;
}

const ROTATION_OPTIONS: RotationOption[] = [
    { label: '0°',    value: '0',   degrees: 0   },
    { label: '90°',   value: '90',  degrees: 90  },
    { label: '180°',  value: '180', degrees: 180 },
    { label: '-90°',  value: '-90', degrees: -90 },
];

const EInkDetail: React.FC<EInkDetailProps> = () => {

    const [t] = useTranslation();
    const { id: einkID } = useParams<{ id: string }>();
    const history = useHistory();

    const [isLoading, setIsLoading]   = useState(false);
    const [isSending, setIsSending]   = useState(false);
    const [eink, setEInk]             = useState<EInkModel>();
    const [selectedPicture, setSelectedPicture] = useState<string | null>(null);
    const [rotation, setRotation]     = useState<string>('0');

    const inputChangeImageRef = useRef<HTMLInputElement>(null);
    const _einkService = new EInkService();

    useEffect(() => {
        _getEInk(einkID);
        return () => { _einkService.cancelRequest(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const _getEInk = async (id: string) => {
        setIsLoading(true);
        try {
            const res: AxiosResponse<ApiResponse<EInkModel>> = await _einkService.show(id);
            setEInk(res.data.data!);
        } catch (error) {
            history.replace('/eink');
        } finally {
            setIsLoading(false);
        }
    };

    const _handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => setSelectedPicture(reader.result?.toString() ?? null);
        reader.readAsDataURL(file);
        // reset so the same file can be re-selected
        e.target.value = '';
    };

    const _handleSend = async () => {
        if (!selectedPicture) return;
        const formData = new FormData();
        formData.append('image', selectedPicture);
        formData.append('rotation', rotation);
        formData.append('type', 'change_image');
        formData.append('dataContentType', 'application/json');
        try {
            setIsSending(true);
            await _einkService.sendChangeImageCommand(einkID, formData);
        } catch (error) {
        } finally {
            setIsSending(false);
        }
    };

    // ── Device info card ───────────────────────────────────────────────────────

    const _renderInfoRow = (label: string, value?: string | null, mono?: boolean) => (
        <div className="eink-detail__info-row">
            <span className="eink-detail__info-label">{label}</span>
            <span className={`eink-detail__info-value${mono ? ' eink-detail__info-value--mono' : ''}${!value || value === '—' ? ' eink-detail__info-value--muted' : ''}`}>
                {value || '—'}
            </span>
        </div>
    );

    const _renderDeviceInfo = () => (
        <div className="card mb-3">
            <div className="card-body">
                <p className="eink-detail__card-title">{t('Device Information')}</p>
                <div className="eink-detail__info-grid">
                    {_renderInfoRow(t('Name'),         eink?.name)}
                    {_renderInfoRow(t('Hardware Key'), eink?.hardware_key, true)}
                    {_renderInfoRow(t('Owner'),        eink?.user_name)}
                    {_renderInfoRow(t('Model'),        undefined)}
                    {_renderInfoRow(t('Firmware'),     undefined)}
                </div>
            </div>
        </div>
    );

    // ── Image / display panel ──────────────────────────────────────────────────

    const _renderImagePanel = () => {
        const currentDeg = ROTATION_OPTIONS.find(r => r.value === rotation)?.degrees ?? 0;
        const isPortrait = Math.abs(currentDeg) === 90;

        return (
            <div className="card">
                <div className="card-body">
                    <p className="eink-detail__card-title">{t('Display Content')}</p>

                    {/* Preview frame */}
                    <div className={`eink-detail__preview-frame${isPortrait ? ' eink-detail__preview-frame--portrait' : ''}`}>
                        <div className="eink-detail__preview-inner">
                            {selectedPicture ? (
                                <img
                                    className="eink-detail__preview-img"
                                    src={selectedPicture}
                                    alt="E-ink preview"
                                    style={{ transform: `rotate(${currentDeg}deg)` }}
                                />
                            ) : (
                                <div className="eink-detail__preview-empty">
                                    <i className="iconsminds-monitor eink-detail__preview-icon" />
                                    <span className="eink-detail__preview-hint">{t('No image selected')}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Rotation selector */}
                    <p className="eink-detail__section-label">{t('Rotation')}</p>
                    <div className="eink-detail__rotation-group">
                        {ROTATION_OPTIONS.map(opt => (
                            <button
                                key={opt.value}
                                type="button"
                                className={`eink-detail__rotation-btn${rotation === opt.value ? ' eink-detail__rotation-btn--active' : ''}`}
                                onClick={() => setRotation(opt.value)}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>

                    {/* Action buttons */}
                    <div className="eink-detail__actions">
                        <input
                            ref={inputChangeImageRef}
                            type="file"
                            accept="image/*"
                            hidden
                            onChange={_handleFileChange}
                        />
                        <button
                            type="button"
                            className="btn btn-outline-secondary eink-detail__action-btn"
                            onClick={() => inputChangeImageRef.current?.click()}
                        >
                            <i className="simple-icon-picture" />
                            <span>{t('Select Image')}</span>
                        </button>
                        <button
                            type="button"
                            className={`btn btn-cactus eink-detail__action-btn${isSending ? ' btn-cactus--loading' : ''}`}
                            disabled={isSending || !selectedPicture}
                            onClick={_handleSend}
                        >
                            {isSending ? t('Sending…') : t('Send to Device')}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // ── Root render ────────────────────────────────────────────────────────────

    return (
        <DashboardWrapper>
            <div className="row align-items-end mb-4">
                <div className="col">
                    <DetailBreadcrumb items={[
                        { label: t('E-Ink Devices'), to: '/eink' },
                        { label: eink?.name ?? t('Electronic Ink') },
                    ]} />
                    <DashboardTitle
                        title={isLoading ? t('Electronic Ink') : (eink?.name ?? t('Electronic Ink'))}
                        isLoading={isLoading}
                    />
                </div>
                {!isLoading && eink && (
                    <div className="col-auto pb-2">
                        <button type="button" className="btn btn-success">
                            {t('Receive')}
                        </button>
                    </div>
                )}
            </div>

            {!isLoading && eink && (
                <div className="row">
                    <div className="col-12 col-md-7 col-xl-8 mb-3">
                        {_renderDeviceInfo()}
                    </div>
                    <div className="col-12 col-md-5 col-xl-4 mb-3">
                        {_renderImagePanel()}
                    </div>
                </div>
            )}
        </DashboardWrapper>
    );
};

export default React.memo(EInkDetail);
